import { metricFormat } from './metricFormat';
import { NetworkAvailableFeature } from '../../auth/networkFeatures';
import type { DashboardMessageKey } from '../../../lib/messages';
import {
  ReportCatalogGroup,
  ReportDimension,
  ReportMetric,
  ReportMetricFormat,
} from './reportEnums';

export interface ReportMetricDefinition {
  metric: ReportMetric;
  field: string;
  format: ReportMetricFormat;
  labelKey: DashboardMessageKey;
  descriptionKey?: DashboardMessageKey | undefined;
  requiredFeature?: NetworkAvailableFeature | undefined;
}
export interface ReportDimensionDefinition {
  dimension: ReportDimension;
  labelKey: DashboardMessageKey;
  requiredFeature?: NetworkAvailableFeature | undefined;
}

export interface ReportCatalogSection<TItem> {
  group: ReportCatalogGroup;
  labelKey: DashboardMessageKey;
  items: readonly TItem[];
}

const fieldOverrides: Partial<Record<ReportMetric, string>> = {
  [ReportMetric.OutgoingFloor]: 'outgoingFloor',
  [ReportMetric.FloorEcpm]: 'floorEcpm',
  [ReportMetric.PublisherNetworkCpm]: 'pubNetworkCpm',
  [ReportMetric.WebVideoPageRequest]: 'wvPageRequests',
  [ReportMetric.DemandPartnerOwnerId]: 'ownerId',
  [ReportMetric.DemandPartnerOwnerName]: 'ownerName',
  [ReportMetric.PublisherRevenueShareIo]: 'pubRevenueShareIo',
  [ReportMetric.ContentRequest]: 'contentRequests',
  [ReportMetric.InsertionRate]: 'insertionRatio',
  [ReportMetric.BidsLi]: 'bidsLineItem',
  [ReportMetric.WonBidsLi]: 'wonBidsLineItem',
  [ReportMetric.FilledImpressionsLi]: 'filledImpressionsLineItem',
  [ReportMetric.AdImpressionsLi]: 'adImpressionsLineItem',
  [ReportMetric.RevenueLi]: 'revenueLineItem',
  [ReportMetric.PubRevenueLi]: 'pubRevenueLineItem',
  [ReportMetric.RpmLi]: 'rpmLineItem',
  [ReportMetric.RenderRateLi]: 'renderRateLineItem',
  [ReportMetric.DataCenter]: 'dataCenterRegion',
  [ReportMetric.AdImpressionsStitchedPct]: 'adStitchedImpressionPct',
};

const descriptionMetrics = new Set<ReportMetric>([
  ReportMetric.OutgoingFloor,
  ReportMetric.FloorEcpm,
  ReportMetric.PublisherNetworkCpm,
  ReportMetric.PublisherCpm,
  ReportMetric.ContentRequest,
  ReportMetric.DpAdRequests,
  ReportMetric.AdUnitImpressions,
  ReportMetric.DpAdRequestRatio,
  ReportMetric.AdRequests,
  ReportMetric.AdImpressions,
  ReportMetric.AdRequestsFilled,
  ReportMetric.FilledImpressions,
  ReportMetric.RenderRate,
  ReportMetric.ResponseRate,
  ReportMetric.Revenue,
  ReportMetric.NetRevenue,
  ReportMetric.PubRevenue,
  ReportMetric.Profit,
  ReportMetric.Rpm,
  ReportMetric.Srpm,
  ReportMetric.NetworkMarginPct,
  ReportMetric.MarginPct,
  ReportMetric.PublisherRevenueShare,
  ReportMetric.Bids,
  ReportMetric.BidFillRatio,
  ReportMetric.WonBids,
  ReportMetric.InsertionRate,
  ReportMetric.WinRatio,
  ReportMetric.BidRatio,
  ReportMetric.LostRatio,
  ReportMetric.LostBids,
  ReportMetric.BidWinLostRatio,
  ReportMetric.ContentImpressions,
  ReportMetric.StichImpressions,
  ReportMetric.Xp,
  ReportMetric.BundleRevenueShare,
  ReportMetric.BundleCpm,
  ReportMetric.NetworkSaasType,
  ReportMetric.NetworkSaasValue,
  ReportMetric.NetworkSaasMetric,
  ReportMetric.NetworkSaasFee,
  ReportMetric.NetworkRevenue,
  ReportMetric.NetworkProfit,
  ReportMetric.AdImpressionsStitchedPct,
  ReportMetric.PubRenderRate,
  ReportMetric.BidCpm,
  ReportMetric.RpmFill,
  ReportMetric.AppName,
]);

export const reportMetricDefinitions = Object.fromEntries(
  Object.values(ReportMetric).map((metric) => [metric, createMetricDefinition(metric)]),
) as Record<ReportMetric, ReportMetricDefinition>;

export const reportDimensionDefinitions = Object.fromEntries(
  Object.values(ReportDimension).map((dimension) => [
    dimension,
    {
      dimension,
      labelKey: `reportExplorer.dimension.${dimension}` as DashboardMessageKey,
      requiredFeature: dimensionFeature(dimension),
    },
  ]),
) as Record<ReportDimension, ReportDimensionDefinition>;

export const reportMetricSections: readonly ReportCatalogSection<ReportMetricDefinition>[] = [
  metricSection(ReportCatalogGroup.General, [ReportMetric.AgencyId, ReportMetric.AgencyName]),
  metricSection(ReportCatalogGroup.Kpi, [
    ReportMetric.IiqResolvedRequests,
    ReportMetric.IiqAdImpressions,
    ReportMetric.AdRequests,
    ReportMetric.ResponseRate,
    ReportMetric.FilledImpressions,
    ReportMetric.RenderRate,
    ReportMetric.AdClicks,
    ReportMetric.Ctr,
    ReportMetric.ClickCpm,
    ReportMetric.AdRequestsFilled,
    ReportMetric.AdImpressions,
    ReportMetric.AdStart,
    ReportMetric.AdMid,
    ReportMetric.AdQuartile1,
    ReportMetric.AdQuartile3,
    ReportMetric.AdCompletion,
    ReportMetric.AdUnitImpressions,
    ReportMetric.PubAdImpressions,
    ReportMetric.PubRenderRate,
    ReportMetric.DpFillRatio,
    ReportMetric.AdImpressionsDvIqm,
    ReportMetric.View,
    ReportMetric.ViewPct,
  ]),
  metricSection(ReportCatalogGroup.Exchange, [
    ReportMetric.DpAdRequests,
    ReportMetric.Bids,
    ReportMetric.BidFillRatio,
    ReportMetric.WonBids,
    ReportMetric.LostBids,
    ReportMetric.BidRatio,
    ReportMetric.WinRatio,
    ReportMetric.LostRatio,
    ReportMetric.BidWinLostRatio,
    ReportMetric.DpAdRequestRatio,
  ]),
  metricSection(ReportCatalogGroup.Finance, [
    ReportMetric.Ecpm,
    ReportMetric.RevenueBids,
    ReportMetric.RevenueFill,
    ReportMetric.PreMpRevenue,

    ReportMetric.IiqRevenue,
    ReportMetric.Revenue,
    ReportMetric.NetworkProfit,
    ReportMetric.Profit,
    ReportMetric.RevenuePct,
    ReportMetric.Rpm,
    ReportMetric.Srpm,
    ReportMetric.RpmFill,
    ReportMetric.RpmNet,
    ReportMetric.PublisherRevenueShare,
    ReportMetric.PubGrossRevenue,
    ReportMetric.PublisherCpm,
    ReportMetric.BundleRevenueShare,
    ReportMetric.BundleCpm,
    ReportMetric.PubRevenue,
    ReportMetric.NetRevenue,
    ReportMetric.NetworkSaasFee,
    ReportMetric.MarginPct,
    ReportMetric.NetworkMarginPct,
    ReportMetric.NetworkRevenue,
    ReportMetric.NetworkRevenuePct,
    ReportMetric.PubRpm,
    ReportMetric.Rps,
    ReportMetric.BidCpm,
    ReportMetric.FillCpm,
    ReportMetric.BidfloorReq,
    ReportMetric.OutgoingFloor,
    ReportMetric.FloorEcpm,
    ReportMetric.LrSegmentCost,
  ]),
  metricSection(ReportCatalogGroup.Content, [
    ReportMetric.ImmuVideoPlayed,
    ReportMetric.Page,
    ReportMetric.ContentRequest,
  ]),
  metricSection(ReportCatalogGroup.Errors, [ReportMetric.Timeouts]),
  metricSection(ReportCatalogGroup.Stitcher, [
    ReportMetric.AdImpressionsNonStitched,
    ReportMetric.FilledImpressionsNonStitched,

    ReportMetric.StichImpressions,
    ReportMetric.FilledImpressionsStitched,
    ReportMetric.AdImpressionsStitched,
    ReportMetric.ContentImpressions,
    ReportMetric.RevenueStitched,
    ReportMetric.StitchCpmCost,
    ReportMetric.RpmStitched,
    ReportMetric.RenderRateStitched,
    ReportMetric.AdImpressionsStitchedPct,
  ]),
  metricSection(ReportCatalogGroup.Cache, [
    ReportMetric.WonBidsCache,
    ReportMetric.FilledImpressionsCache,
    ReportMetric.AdImpressionsCache,
    ReportMetric.RevenueCache,
    ReportMetric.PubRevenueCache,
  ]),
  metricSection(ReportCatalogGroup.Network, [
    ReportMetric.NetworkSaasType,
    ReportMetric.NetworkSaasValue,
    ReportMetric.NetworkSaasMetric,
    ReportMetric.NetworkSaasFee,
    ReportMetric.NetworkRevenue,
    ReportMetric.NetworkProfit,
    ReportMetric.NetworkMarginPct,
    ReportMetric.NetworkRevenuePct,
  ]),
  metricSection(ReportCatalogGroup.Campaigns, [
    ReportMetric.BidsLi,
    ReportMetric.WonBidsLi,
    ReportMetric.FilledImpressionsLi,
    ReportMetric.AdImpressionsLi,
    ReportMetric.RevenueLi,
    ReportMetric.PubRevenueLi,
    ReportMetric.RpmLi,
    ReportMetric.RenderRateLi,
  ]),
  metricSection(ReportCatalogGroup.WebVideo, [ReportMetric.WebVideoPageRequest]),
  metricSection(ReportCatalogGroup.Ivt, [
    ReportMetric.IvtTotalEvents,
    ReportMetric.IvtInvalidTraffic,
    ReportMetric.IvtInvalidTrafficPct,
    ReportMetric.IvtSofisticated,
    ReportMetric.IvtSofisticatedPct,
    ReportMetric.IvtGeneral,
    ReportMetric.IvtGeneralPct,
    ReportMetric.IvtValidTraffic,
    ReportMetric.IvtValidTrafficPct,
  ]),
];

export const reportDimensionSections: readonly ReportCatalogSection<ReportDimensionDefinition>[] = [
  dimensionSection(ReportCatalogGroup.TimeGranularity, [
    ReportDimension.Year,
    ReportDimension.Month,
    ReportDimension.Day,
    ReportDimension.Hour,
  ]),
  dimensionSection(ReportCatalogGroup.General, [
    ReportDimension.Video,
    ReportDimension.Marketplace,
    ReportDimension.IiqArm,
    ReportDimension.Browser,
    ReportDimension.Network,
    ReportDimension.Agency,
    ReportDimension.Publisher,
    ReportDimension.PublisherDomain,
    ReportDimension.App,
    ReportDimension.BidderCode,
    ReportDimension.DemandPartner,
    ReportDimension.DemandPartnerOwner,
    ReportDimension.PublisherPage,
    ReportDimension.Xp,
    ReportDimension.Xp1,
    ReportDimension.Xp2,
    ReportDimension.Xp3,
    ReportDimension.SchainAsi,
    ReportDimension.Adomain,
    ReportDimension.SchainNodeDomain,
    ReportDimension.Placement,
    ReportDimension.LineItem,
    ReportDimension.Order,
    ReportDimension.DemandIntegration,
    ReportDimension.Country,
    ReportDimension.Region,
    ReportDimension.DataCenter,
    ReportDimension.ErrorCode,
    ReportDimension.Platform,
    ReportDimension.AppStore,
    ReportDimension.DvSegment,
    ReportDimension.Deal,
    ReportDimension.AuctionType,
    ReportDimension.Advertiser,
    ReportDimension.Brand,
    ReportDimension.BidfloorReq,
    ReportDimension.Creative,
    ReportDimension.CrId,
    ReportDimension.LrSegment,
    ReportDimension.Application,
    ReportDimension.IvtProvider,
    ReportDimension.IvtType,
    ReportDimension.Omidpn,
    ReportDimension.Omidpv,
  ]),
  dimensionSection(ReportCatalogGroup.Content, [
    ReportDimension.ContentType,
    ReportDimension.ContentTitle,
    ReportDimension.ContentSeries,
    ReportDimension.ContentGenre,
    ReportDimension.ContentCat,
    ReportDimension.ContentRating,
    ReportDimension.ContentChannelName,
    ReportDimension.ContentNetworkName,
    ReportDimension.ContentLanguage,
    ReportDimension.ContentLivestream,
  ]),
];

export function isNumericReportMetric(metric: ReportMetric) {
  return (
    reportMetricDefinitions[metric].format !== ReportMetricFormat.String &&
    reportMetricDefinitions[metric].format !== ReportMetricFormat.DateTime
  );
}

function createMetricDefinition(metric: ReportMetric): ReportMetricDefinition {
  return {
    metric,
    field: fieldOverrides[metric] ?? snakeToCamel(metric),
    format: metricFormat(metric),
    labelKey: `reportExplorer.metric.${metric}` as DashboardMessageKey,
    ...(descriptionMetrics.has(metric)
      ? { descriptionKey: `reportExplorer.metricDescription.${metric}` as DashboardMessageKey }
      : {}),
    ...(metric === ReportMetric.AdUnitImpressions
      ? { requiredFeature: NetworkAvailableFeature.AdUnit }
      : {}),
  };
}

function metricSection(
  group: ReportCatalogGroup,
  metrics: readonly ReportMetric[],
): ReportCatalogSection<ReportMetricDefinition> {
  return {
    group,
    labelKey: `reportExplorer.group.${group}` as DashboardMessageKey,
    items: metrics.map((metric) => reportMetricDefinitions[metric]),
  };
}

function dimensionSection(
  group: ReportCatalogGroup,
  dimensions: readonly ReportDimension[],
): ReportCatalogSection<ReportDimensionDefinition> {
  return {
    group,
    labelKey: `reportExplorer.group.${group}` as DashboardMessageKey,
    items: dimensions.map((dimension) => reportDimensionDefinitions[dimension]),
  };
}

function dimensionFeature(dimension: ReportDimension) {
  return [
    ReportDimension.ContentType,
    ReportDimension.ContentTitle,
    ReportDimension.ContentSeries,
    ReportDimension.ContentGenre,
    ReportDimension.ContentCat,
    ReportDimension.ContentRating,
    ReportDimension.ContentChannelName,
    ReportDimension.ContentNetworkName,
    ReportDimension.ContentLanguage,
    ReportDimension.ContentLivestream,
    ReportDimension.Order,
    ReportDimension.Advertiser,
    ReportDimension.Brand,
    ReportDimension.LineItem,
  ].includes(dimension)
    ? NetworkAvailableFeature.Campaign
    : undefined;
}

function snakeToCamel(value: string) {
  return value.toLowerCase().replace(/_([a-z0-9])/g, (_, letter: string) => letter.toUpperCase());
}
